/**
 * Multiplayer Poker Service
 *
 * Extends GenericMultiplayerService for Poker-specific multiplayer.
 * Handles room creation/joining, betting action sync, hand management,
 * and reconnection with exponential backoff.
 *
 * Requirements: 26.1–26.8, 27.1–27.7, 28.1–28.6
 */

import { WebSocketService } from '../../services/WebSocketService';
import {
  GenericMultiplayerService,
  GameResult,
} from '../../services/multiplayer/GenericMultiplayerService';
import {
  PokerGameState,
  PlayerAction,
  VisibleGameState,
  Card,
  ShowdownResult,
  PotInfo,
  ActionRecord,
} from '../types';

/** Poker-specific action sent over WebSocket */
export interface PokerAction {
  type: PlayerAction['type'];
  amount?: number;
  playerId: string;
}

/** New hand state from server */
export interface NewHandState {
  handNumber: number;
  dealerSeatIndex: number;
  smallBlindSeatIndex: number;
  bigBlindSeatIndex: number;
  holeCards: Card[];
  blindLevel: { smallBlind: number; bigBlind: number };
}

const RECONNECT_BASE_DELAY_MS = 1000;
const RECONNECT_MAX_ATTEMPTS = 3;
const GAME_STATE_RETENTION_MS = 5 * 60 * 1000; // 5 minutes

export class MultiplayerPokerService extends GenericMultiplayerService<
  PokerGameState,
  PokerAction
> {
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private disconnectedAt: number | null = null;
  private playerSeat: number | null = null;
  private onReconnectionFailedCallback?: () => void;

  // Poker-specific callbacks
  private onHoleCardsDealtCallback?: (cards: Card[]) => void;
  private onCommunityCardsDealtCallback?: (cards: Card[], phase: string) => void;
  private onPlayerActionCallback?: (action: ActionRecord) => void;
  private onPotUpdatedCallback?: (pots: PotInfo[]) => void;
  private onShowdownCallback?: (result: ShowdownResult) => void;
  private onHandCompleteCallback?: (distributions: any) => void;
  private onPlayerEliminatedCallback?: (playerId: string) => void;
  private onNewHandCallback?: (handState: NewHandState) => void;
  private onTurnTimeoutCallback?: (playerId: string, countdown: number) => void;

  constructor(wsService?: WebSocketService) {
    super(wsService ?? WebSocketService.getInstance(), 'poker');
  }

  // -----------------------------------------------------------------------
  // Abstract method implementations
  // -----------------------------------------------------------------------

  protected setupMessageHandlers(): void {
    this.wsService.setCustomMessageHandler((message: any) => {
      try {
        const type: string = message.type ?? '';

        switch (type) {
          case 'poker:roomCreated':
            this.roomCode = message.roomCode ?? null;
            this.playerRole = message.seatIndex?.toString() ?? 'seat-0';
            this.playerSeat = message.seatIndex ?? 0;
            if (this.onGameStartedCallback) {
              this.onGameStartedCallback();
            }
            break;

          case 'poker:roomJoined':
            this.roomCode = message.roomCode ?? null;
            this.playerRole = message.seatIndex?.toString() ?? 'seat-1';
            this.playerSeat = message.seatIndex ?? 1;
            if (this.onGameStartedCallback) {
              this.onGameStartedCallback();
            }
            break;

          case 'poker:opponentJoined':
            if (this.onPlayerConnectedCallback) {
              this.onPlayerConnectedCallback(message.playerId ?? 'opponent');
            }
            if (this.onGameStartedCallback) {
              this.onGameStartedCallback();
            }
            break;

          case 'poker:playerJoined':
            if (this.onPlayerConnectedCallback) {
              this.onPlayerConnectedCallback(message.playerId ?? 'unknown');
            }
            break;

          case 'poker:gameStarted':
            if (message.gameState) {
              this.gameState = message.gameState;
            }
            if (this.onGameStateReceivedCallback) {
              this.onGameStateReceivedCallback(message.gameState);
            }
            break;

          case 'poker:newHand':
            if (this.onNewHandCallback) {
              this.onNewHandCallback({
                handNumber: message.handNumber,
                dealerSeatIndex: message.dealerSeatIndex,
                smallBlindSeatIndex: message.smallBlindSeatIndex,
                bigBlindSeatIndex: message.bigBlindSeatIndex,
                holeCards: message.holeCards ?? [],
                blindLevel: message.blindLevel,
              });
            }
            if (message.holeCards && this.onHoleCardsDealtCallback) {
              this.onHoleCardsDealtCallback(message.holeCards);
            }
            break;

          case 'poker:communityCards':
            if (this.onCommunityCardsDealtCallback) {
              this.onCommunityCardsDealtCallback(
                message.cards ?? [],
                message.phase ?? 'flop'
              );
            }
            break;

          case 'poker:actionRequired':
            // Server tells this client it's their turn
            if (message.gameState) {
              this.gameState = message.gameState;
            }
            if (this.onGameStateReceivedCallback) {
              this.onGameStateReceivedCallback(message.gameState);
            }
            break;

          case 'poker:playerActed':
            if (this.onPlayerActionCallback) {
              this.onPlayerActionCallback(message.action);
            }
            break;

          case 'poker:potUpdated':
            if (this.onPotUpdatedCallback) {
              this.onPotUpdatedCallback(message.pots ?? []);
            }
            break;

          case 'poker:showdown':
            if (this.onShowdownCallback) {
              this.onShowdownCallback(message.result);
            }
            break;

          case 'poker:handComplete':
            if (this.onHandCompleteCallback) {
              this.onHandCompleteCallback(message.distributions);
            }
            break;

          case 'poker:playerEliminated':
            if (this.onPlayerEliminatedCallback) {
              this.onPlayerEliminatedCallback(message.playerId);
            }
            break;

          case 'poker:gameOver': {
            const gameOverResult: GameResult = {
              result: message.result ?? 'win',
              winner: message.winner,
              reason: message.reason,
            };
            if (this.onGameOverCallback) {
              this.onGameOverCallback(gameOverResult);
            }
            break;
          }

          case 'poker:turnTimeout':
            if (this.onTurnTimeoutCallback) {
              this.onTurnTimeoutCallback(
                message.playerId ?? '',
                message.countdown ?? 30
              );
            }
            break;

          case 'poker:opponentDisconnected':
            if (this.onPlayerDisconnectedCallback) {
              this.onPlayerDisconnectedCallback(message.playerId ?? 'opponent');
            }
            break;

          case 'poker:opponentReconnected':
            if (this.onPlayerConnectedCallback) {
              this.onPlayerConnectedCallback(message.playerId ?? 'opponent');
            }
            break;

          case 'poker:gameState':
            if (message.gameState) {
              this.gameState = message.gameState;
            }
            if (this.onGameStateReceivedCallback) {
              this.onGameStateReceivedCallback(message.gameState);
            }
            this.resetReconnectionState();
            if (this.onConnectionStatusCallback) {
              this.onConnectionStatusCallback('connected');
            }
            break;

          case 'poker:error':
            this.handleError(message.message ?? message.error ?? 'Unknown poker error');
            break;

          default:
            break;
        }
      } catch (err) {
        this.handleError('Error handling poker message', err);
      }
    });
  }

  protected sendCreateRoomMessage(): void {
    this.wsService.send({
      action: 'poker:createRoom',
      playerId: this.playerId,
    });
  }

  protected sendJoinRoomMessage(roomCode: string): void {
    this.wsService.send({
      action: 'poker:joinRoom',
      roomCode,
      playerId: this.playerId,
    });
  }

  protected sendLeaveRoomMessage(): void {
    if (this.roomCode) {
      this.wsService.send({
        action: 'poker:leaveRoom',
        roomCode: this.roomCode,
        playerId: this.playerId,
      });
    }
  }

  protected getMaxPlayers(): number {
    return 8;
  }

  // -----------------------------------------------------------------------
  // Move sending
  // -----------------------------------------------------------------------

  async sendMove(move: PokerAction): Promise<boolean> {
    try {
      if (!this.roomCode) {
        throw new Error('Not in a multiplayer game');
      }

      this.wsService.send({
        action: 'poker:action',
        roomCode: this.roomCode,
        playerId: this.playerId,
        actionType: move.type,
        amount: move.amount,
      });

      return true;
    } catch (error) {
      this.handleError('Failed to send poker action', error);
      return false;
    }
  }

  // -----------------------------------------------------------------------
  // Start game (room creator)
  // -----------------------------------------------------------------------

  async sendStartGame(): Promise<boolean> {
    try {
      if (!this.roomCode) {
        throw new Error('Not in a room');
      }

      this.wsService.send({
        action: 'poker:startGame',
        roomCode: this.roomCode,
        playerId: this.playerId,
      });

      return true;
    } catch (error) {
      this.handleError('Failed to start game', error);
      return false;
    }
  }

  // -----------------------------------------------------------------------
  // Turn check
  // -----------------------------------------------------------------------

  isMyTurn(currentTurn: string): boolean {
    return this.playerId === currentTurn;
  }

  getPlayerSeat(): number | null {
    return this.playerSeat;
  }

  // -----------------------------------------------------------------------
  // Resign
  // -----------------------------------------------------------------------

  async resign(): Promise<void> {
    if (!this.roomCode) {
      throw new Error('Not in a multiplayer game');
    }
    this.wsService.send({
      action: 'poker:resign',
      roomCode: this.roomCode,
      playerId: this.playerId,
    });
  }

  // -----------------------------------------------------------------------
  // Poker-specific event handler setters
  // -----------------------------------------------------------------------

  onHoleCardsDealt(callback: (cards: Card[]) => void): void {
    this.onHoleCardsDealtCallback = callback;
  }

  onCommunityCardsDealt(callback: (cards: Card[], phase: string) => void): void {
    this.onCommunityCardsDealtCallback = callback;
  }

  onPlayerAction(callback: (action: ActionRecord) => void): void {
    this.onPlayerActionCallback = callback;
  }

  onPotUpdated(callback: (pots: PotInfo[]) => void): void {
    this.onPotUpdatedCallback = callback;
  }

  onShowdown(callback: (result: ShowdownResult) => void): void {
    this.onShowdownCallback = callback;
  }

  onHandComplete(callback: (distributions: any) => void): void {
    this.onHandCompleteCallback = callback;
  }

  onPlayerEliminated(callback: (playerId: string) => void): void {
    this.onPlayerEliminatedCallback = callback;
  }

  onNewHand(callback: (handState: NewHandState) => void): void {
    this.onNewHandCallback = callback;
  }

  onTurnTimeout(callback: (playerId: string, countdown: number) => void): void {
    this.onTurnTimeoutCallback = callback;
  }

  // -----------------------------------------------------------------------
  // Connection management & exponential backoff reconnection
  // -----------------------------------------------------------------------

  handleDisconnect(): void {
    this.disconnectedAt = Date.now();

    if (this.onConnectionStatusCallback) {
      this.onConnectionStatusCallback('disconnected');
    }

    this.attemptReconnection();
  }

  private attemptReconnection(): void {
    if (this.reconnectAttempts >= RECONNECT_MAX_ATTEMPTS) {
      if (this.onReconnectionFailedCallback) {
        this.onReconnectionFailedCallback();
      }
      this.handleError(
        'Max reconnection attempts reached. Please reconnect manually or return to mode selection.'
      );
      return;
    }

    const delay = RECONNECT_BASE_DELAY_MS * Math.pow(2, this.reconnectAttempts);
    this.reconnectAttempts++;

    if (this.onConnectionStatusCallback) {
      this.onConnectionStatusCallback('reconnecting');
    }

    this.reconnectTimer = setTimeout(async () => {
      try {
        await this.wsService.connect();

        if (this.roomCode && this.playerId) {
          this.wsService.send({
            action: 'poker:reconnect',
            roomCode: this.roomCode,
            playerId: this.playerId,
          });
        }
      } catch {
        this.attemptReconnection();
      }
    }, delay);
  }

  isGameStateExpired(): boolean {
    if (this.disconnectedAt === null) return false;
    return Date.now() - this.disconnectedAt > GAME_STATE_RETENTION_MS;
  }

  getReconnectAttempts(): number {
    return this.reconnectAttempts;
  }

  onReconnectionFailed(callback: () => void): void {
    this.onReconnectionFailedCallback = callback;
  }

  // -----------------------------------------------------------------------
  // Cleanup
  // -----------------------------------------------------------------------

  protected cleanup(): void {
    this.resetReconnectionState();
    this.disconnectedAt = null;
    this.playerSeat = null;
    this.onHoleCardsDealtCallback = undefined;
    this.onCommunityCardsDealtCallback = undefined;
    this.onPlayerActionCallback = undefined;
    this.onPotUpdatedCallback = undefined;
    this.onShowdownCallback = undefined;
    this.onHandCompleteCallback = undefined;
    this.onPlayerEliminatedCallback = undefined;
    this.onNewHandCallback = undefined;
    this.onTurnTimeoutCallback = undefined;
    super.cleanup();
  }

  private resetReconnectionState(): void {
    this.reconnectAttempts = 0;
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }
}
